import { useSyncExternalStore } from 'react';

import { z } from 'zod';

const STORAGE_KEY = 'opendoor.cosmos.marketplace.viewed-properties';
const MAX_VIEWED_PROPERTY_IDS = 1000;
const VIEWED_PROPERTY_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;
const viewedPropertySchema = z.object({
  addressId: z.string().min(1),
  lastViewedAtMs: z.number().int().nonnegative(),
});
const storedPropertiesSchema = z.union([
  z.array(viewedPropertySchema),
  z.array(z.string().min(1)).transform((ids) => {
    const lastViewedAtMs = Date.now();
    return ids.map((addressId) => ({ addressId, lastViewedAtMs }));
  }),
]);
type ViewedProperty = z.infer<typeof viewedPropertySchema>;

const EMPTY_IDS: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();
let properties: ViewedProperty[] = [];
let viewedPropertyIds = EMPTY_IDS;
let storageValue: string | null | undefined;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

const normalizeProperties = (entries: ViewedProperty[], now: number): ViewedProperty[] => {
  const byAddress = new Map<string, ViewedProperty>();
  for (const { addressId, lastViewedAtMs: storedAtMs } of entries) {
    // A future timestamp (clock moved backward) is clamped to now, so the view is kept but cannot
    // outlive the 30-day lifetime.
    const lastViewedAtMs = Math.min(storedAtMs, now);
    if (lastViewedAtMs + VIEWED_PROPERTY_TTL_MS <= now) continue;
    const previous = byAddress.get(addressId);
    if (!previous || lastViewedAtMs > previous.lastViewedAtMs)
      byAddress.set(addressId, { addressId, lastViewedAtMs });
  }
  // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos still targets browsers without Array.prototype.toSorted; this sorts the spread copy.
  const sorted = [...byAddress.values()].sort((a, b) => a.lastViewedAtMs - b.lastViewedAtMs);
  return sorted.slice(-MAX_VIEWED_PROPERTY_IDS);
};

const parseStoredProperties = (value: string | null): ViewedProperty[] | undefined => {
  if (value === null) return [];
  let stored: unknown;
  try {
    stored = JSON.parse(value);
  } catch {
    return undefined;
  }
  const result = storedPropertiesSchema.safeParse(stored);
  return result.success ? result.data : undefined;
};

const setProperties = (entries: ViewedProperty[]) => {
  properties = entries;
  if (
    entries.length !== viewedPropertyIds.size ||
    entries.some(({ addressId }) => !viewedPropertyIds.has(addressId))
  ) {
    viewedPropertyIds = new Set(entries.map(({ addressId }) => addressId));
  }
};

const persistProperties = () => {
  const value = JSON.stringify(properties);
  if (value === storageValue) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    storageValue = value;
  } catch {
    // The current page can still distinguish viewed homes without persistence.
  }
};

const readViewedPropertyIds = (): ReadonlySet<string> => {
  if (typeof window === 'undefined') return EMPTY_IDS;
  let value: string | null | undefined;
  try {
    value = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Expiry still applies to browser memory when storage is unavailable.
  }
  if (value !== undefined && value !== storageValue) {
    storageValue = value;
    const stored = parseStoredProperties(value);
    setProperties(stored ? normalizeProperties(stored, Date.now()) : []);
    // History that cannot be read stays in storage until a later view replaces it.
    if (stored && value !== null) persistProperties();
  }
  const now = Date.now();
  if (properties[0] && properties[0].lastViewedAtMs + VIEWED_PROPERTY_TTL_MS <= now) {
    setProperties(
      properties.filter(({ lastViewedAtMs }) => lastViewedAtMs + VIEWED_PROPERTY_TTL_MS > now),
    );
    persistProperties();
  }
  return viewedPropertyIds;
};

const notify = () => listeners.forEach((listener) => listener());
// Timers can pause while the device sleeps, so every refresh schedules again from the current time.
const scheduleExpiry = () => {
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
  const expiryAtMs = properties[0]?.lastViewedAtMs;
  if (listeners.size === 0 || expiryAtMs === undefined) return;
  const nextExpiryAtMs = expiryAtMs + VIEWED_PROPERTY_TTL_MS;
  expiryTimer = setTimeout(
    () => {
      expiryTimer = undefined;
      refresh();
    },
    Math.min(MAX_TIMEOUT_DELAY_MS, Math.max(0, nextExpiryAtMs - Date.now())),
  );
};
const refresh = () => {
  readViewedPropertyIds();
  scheduleExpiry();
  notify();
};
const handleStorage = (event: StorageEvent) => {
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  refresh();
};
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') refresh();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  refresh();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(expiryTimer);
      expiryTimer = undefined;
    }
  };
};

export const rememberViewedProperty = (addressId: string): void => {
  if (typeof window === 'undefined' || !addressId) return;
  readViewedPropertyIds();
  const now = Date.now();
  setProperties(normalizeProperties([...properties, { addressId, lastViewedAtMs: now }], now));
  persistProperties();
  scheduleExpiry();
  notify();
};

export const useViewedPropertyIds = (): ReadonlySet<string> =>
  useSyncExternalStore(subscribe, readViewedPropertyIds, () => EMPTY_IDS);
